import type { ILoadingStatus } from "@helium10/re-core";
import { getAccountId } from "@helium10/re-core";
import { create } from "zustand";
import type { IListParams, IUploadHistoryItem, IDetailParams, IDetailResponse } from "./request";
import { getUploadHistory, getUploadHistoryDetail } from "./request";

/**
 * 当前页面类型
 */
export type CurrentPage = "list" | "detail";

/**
 * 详情页标签页类型
 */
export type DetailTab = "failed" | "successful";

/**
 * UploadHistory Store 状态
 */
export interface IUploadHistoryStore {
  // 抽屉控制
  isOpen: boolean;
  currentPage: CurrentPage;

  // 列表页数据
  listData: IUploadHistoryItem[];
  listTotalCount: number;
  listLoading: ILoadingStatus;

  // 列表页筛选参数
  listParams: IListParams;

  // 详情页数据
  detailData: IDetailResponse["data"] | null;
  detailLoading: ILoadingStatus;
  currentBatchId: string | null;

  // 详情页状态
  activeTab: DetailTab;
  detailPageNum: number;
  detailPageSize: number;

  // Actions
  openDrawer: () => void;
  closeDrawer: () => void;
  setCurrentPage: (page: CurrentPage) => void;
  fetchListData: (batchId?: string) => Promise<void>;
  fetchDetailData: (batchId: string) => Promise<void>;
  updateListFilter: (params: Partial<IListParams>) => void;
  setActiveTab: (tab: DetailTab) => void;
  updateDetailPagination: (pageNum: number, pageSize?: number) => void;
  resetDetailState: () => void;
}

const defaultListParams: IListParams = {
  accountId: getAccountId() as string,
  pageNum: 1,
  pageSize: 20,
  orderBy: "endTime",
  orderType: "desc",
  createStart: "",
  createEnd: "",
};

/**
 * UploadHistory Store
 */
export const useUploadHistoryStore = create<IUploadHistoryStore>((set, get) => ({
  // 初始状态
  isOpen: false,
  currentPage: "list",
  listData: [],
  listTotalCount: 0,
  listLoading: "init",
  listParams: defaultListParams,
  detailData: null,
  detailLoading: "init",
  currentBatchId: null,
  activeTab: "failed",
  detailPageNum: 1,
  detailPageSize: 20,

  // 打开抽屉
  openDrawer: () => {
    set({ isOpen: true, currentPage: "list" });
    // 打开时自动获取列表数据
    get().fetchListData();
  },

  // 关闭抽屉
  closeDrawer: () => {
    set({
      isOpen: false,
      currentPage: "list",
      listData: [],
      listTotalCount: 0,
      listLoading: "init",
      listParams: defaultListParams,
    });
    get().resetDetailState();
  },

  // 设置当前页面
  setCurrentPage: (page: CurrentPage) => {
    set({ currentPage: page });
  },

  // 获取列表数据
  fetchListData: async (batchId?: string) => {
    const state = get();
    if (!batchId) {
      set({ listLoading: "loading" });
    }

    try {
      const params = {
        ...state.listParams,
        accountId: getAccountId() as string,
      };
      const response = await getUploadHistory(params);

      if (response.success && response.data) {
        // 有 batchId 时，更新对应那一条的数据，否则返回新的数据
        const listData = batchId
          ? state.listData.map((item, index): IUploadHistoryItem => {
              if (item.batchId === batchId) {
                const newItem = (response.data.data.find(
                  (newItem) => newItem.batchId === batchId,
                ) ?? {}) as IUploadHistoryItem;
                return {
                  ...item,
                  ...newItem,
                  id: item.batchId || `item-${index}`,
                  _renderKey: "uploadHistoryItem",
                };
              }
              return item;
            })
          : (response.data.data || []).map((item, index) => ({
              ...item,
              id: item.batchId || `item-${index}`,
              _renderKey: "uploadHistoryItem",
            }));
        set({
          listData,
          listTotalCount: response.data.totalCount || 0,
          listLoading: "loaded",
        });
      } else {
        set({ listLoading: "error" });
      }
    } catch (error) {
      console.error("Failed to fetch upload history list:", error);
      set({ listLoading: "error" });
    }
  },

  // 获取详情数据
  fetchDetailData: async (batchId: string) => {
    set({ detailLoading: "loading", currentBatchId: batchId });

    try {
      const params: IDetailParams = {
        accountId: getAccountId() as string,
        batchId,
      };
      const response = await getUploadHistoryDetail(params);

      if (response.success && response.data) {
        set({
          detailData: response.data,
          detailLoading: "loaded",
          activeTab: response.data.failedTotal > 0 ? "failed" : "successful",
          detailPageNum: 1, // 重置到第一页
        });
      } else {
        set({ detailLoading: "error" });
      }
    } catch (error) {
      console.error("Failed to fetch upload history detail:", error);
      set({ detailLoading: "error" });
    }
  },

  // 更新列表筛选参数
  updateListFilter: (params: Partial<IListParams>) => {
    set((state) => ({
      listParams: {
        ...state.listParams,
        ...params,
      },
    }));
    // 自动刷新列表数据
    get().fetchListData();
  },

  // 设置详情页标签页
  setActiveTab: (tab: DetailTab) => {
    set({ activeTab: tab, detailPageNum: 1 }); // 切换标签时重置到第一页
  },

  // 更新详情页分页
  updateDetailPagination: (pageNum: number, pageSize?: number) => {
    set((state) => ({
      detailPageNum: pageNum,
      detailPageSize: pageSize ?? state.detailPageSize,
    }));
  },

  // 重置详情页状态
  resetDetailState: () => {
    set({
      detailData: null,
      detailLoading: "init",
      currentBatchId: null,
      activeTab: "failed",
      detailPageNum: 1,
      detailPageSize: 20,
    });
  },
}));
